import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const QuestionRow = ({ question, questionNumber, isExample, answer, handleInputChange, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const parts = question.question_text.split('{{big_gap}}');
  const labelText = parts[0];
  const postGapText = parts.length > 1 ? parts[1] : null;

  // Split labelText by ':' to align parts separately
  const labelParts = labelText.split(':');
  const hasColon = labelParts.length > 1;
  const leftPart = labelParts[0];
  const rightPart = hasColon ? labelParts[1] : null;

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
      <span className="w-6 font-bold text-primary-blue text-lg">{isExample ? '' : `${questionNumber}.`}</span>
      <label className="flex-1 flex justify-between items-baseline font-body font-bold text-slate-600 dark:text-slate-300 pr-2" htmlFor={question.id}>
        <span className="text-left">{leftPart}{hasColon ? ':' : ''}</span>
        {rightPart && <span className="text-right text-sm opacity-80">{rightPart}</span>}
      </label>
      <div className="w-48 md:w-72 flex-shrink-0 flex items-center gap-3">
        {isExample ? (
          <input
            type="text"
            value={question.accepted_answers[0]}
            readOnly
            disabled
            className="flex-grow exercise-input-text exercise-example-text bg-transparent border-none px-3 py-2 outline-none"
          />
        ) : (
          <>
            {showIncorrectAnswerInReview ? (
              <div className="exercise-input-text bg-red-50 px-3 py-1 rounded-lg">
                <span className="text-red-500 line-through decoration-2 mr-2">{question.user_answer?.answer || '(empty)'}</span>
                <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
              </div>
            ) : (
              <input
                type="text"
                id={question.id}
                autoComplete="off"
                value={showHint || (isReviewMode && isAnswerCorrect) ? question.accepted_answers[0] : (answer || '')}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className={`flex-grow bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 focus:ring-4 focus:ring-blue-100 focus:border-primary-blue rounded-xl px-4 py-2 font-bold text-primary-blue dark:text-blue-400 transition-all outline-none ${showHint ? 'hint-input' : ''}`}
                readOnly={showHint || !isHydrated}
                disabled={isReviewMode || !isHydrated}
              />
            )}
            {isReviewMode && ((isCorrect === true || isCorrect === 'true')
              ? <span className="correct-checkmark">✓</span>
              : <span className="incorrect-checkmark">✗</span>
            )}
          </>
        )}
        {postGapText && <span className="font-body text-slate-600 dark:text-slate-400">{postGapText}</span>}
      </div>
    </div>
  );
};

const ListenPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'string') ? value : value.answer;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    story_title,
  } = exerciseData.content_data;

  const { questions } = exerciseData;
  const exampleQuestion = questions.find(q => q.order_index === 0);
  const mainQuestions = questions.filter(q => q.order_index > 0).sort((a, b) => a.order_index - b.order_index);
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-3">
      <div className="relative flex flex-col h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-4 md:p-6 overflow-y-auto custom-scrollbar">
        {image_url && (
          <div className="bg-white dark:bg-card-dark p-2 rounded-2xl shadow-soft border-2 border-white dark:border-card-dark overflow-hidden shrink-0 mb-4 mx-auto w-max">
            <img
              src={`${ASSET_BASE_URL}${image_url}`}
              alt="Exercise illustration"
              className="mx-auto rounded-xl w-auto max-w-full max-h-40 object-contain shadow-sm"
            />
          </div>
        )}

        <div className="max-w-3xl mx-auto w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-6 md:p-8 text-center shadow-sm">
          {story_title && <h2 className="exercise-story-title">{story_title}</h2>}
          <div className="space-y-1 text-left">
            {exampleQuestion &&
              <QuestionRow
                question={exampleQuestion}
                isExample={true}
              />}
            {mainQuestions.map((q, index) => (
              <QuestionRow
                key={q.id}
                question={q}
                questionNumber={index + 1}
                isExample={false}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListenPart2;